// Single source of truth for the homepage FAQ.
//
// Google's FAQPage guidelines require every Question/Answer in the structured
// data to be visibly present on the page. Rendering and markup therefore read
// from the SAME array — `FAQSection` renders it, `buildFAQPageJsonLd` marks it
// up. Never hand-write a second FAQ list in JSON-LD.

import { PRICING_LADDER_SUMMARY } from "@/content/offers";

export type FAQItem = { q: string; a: string };

export const HOME_FAQ: FAQItem[] = [
  {
    q: "Who is Dr. Sully?",
    a: "Dr. RD “Sully” Sullivan is an AI executive coach, keynote speaker, organizational psychologist, and CEO advisor. He’s the creator of the MAKECHANGE Framework and founder of He Speaks Organic Coaching & Consulting LLC.",
  },
  {
    q: "What does Dr. Sully coach on?",
    a: "Executive identity, decision velocity, trauma-informed leadership, AI strategy, and culture transformation — for founders, CEOs, pastors, and senior operators carrying weight that has to last.",
  },
  {
    q: "How do I work with Dr. Sully 1:1?",
    a: "Apply through the inquiry form. Coaching is by application with a six-month minimum and a limited number of seats per quarter. We’ll send a short scoping call invite if it’s a fit.",
  },
  {
    q: "How do I book Dr. Sully to speak?",
    a: "Submit a speaking inquiry at /speaking or call (855) 76-COACH. Keynote engagements start at $15K and customize from there for travel, audience, and pre-work.",
  },
  {
    q: "Where is Dr. Sully based, and does he travel?",
    a: "He Speaks Organic Coaching & Consulting is based at 1400 West Ave, San Antonio, TX 78201. Coaching runs virtually worldwide, and keynotes, workshops, and org engagements travel. Reach the office at (855) 762-6224.",
  },
  {
    q: "Is the MAKECHANGE Circle right for me?",
    a: "Yes if you want monthly live teaching, real Q&A, and a community of builders, pastors, and operators walking the same pillars. It’s the lowest-friction way in. Cancel anytime.",
  },
  {
    q: "Do you work with churches and faith-led organizations?",
    a: "Yes — extensively. Faith-safe and faith-informed is part of the work, not a side note. Trauma-informed change management and pastoral fluency come standard. We work with individuals and organizations of any denomination, gender, or background — no discrimination, ever.",
  },
  {
    q: "Are results guaranteed?",
    a: "Outcomes depend on the work you’re willing to do. Dr. Sully brings the framework, the questions, and the playbook — implementation is yours. Most clients report meaningful shifts inside the first 60 days.",
  },
  {
    q: "What does pricing look like?",
    a: `Four pathways: ${PRICING_LADDER_SUMMARY}. Pick the entry point that fits your season — every pathway compounds into the next.`,
  },
];

/**
 * Builds a Rich-Results-clean FAQPage node from rendered FAQ items.
 * `text` stays plain text — Google rejects control characters and strips
 * unsupported HTML, so we normalise whitespace instead of embedding markup.
 */
export const buildFAQPageJsonLd = (items: FAQItem[], id: string) => ({
  "@type": "FAQPage" as const,
  "@id": id,
  url: id,
  mainEntity: items.map((item) => ({
    "@type": "Question" as const,
    name: item.q.replace(/\s+/g, " ").trim(),
    acceptedAnswer: {
      "@type": "Answer" as const,
      text: item.a.replace(/\s+/g, " ").trim(),
    },
  })),
});
